import { useCallback, useEffect, useRef, useState } from 'react';

import Modal from './Modal';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import './SelectOrderedSubList.css';

const AvailableListItem = ({ selectHandler, label, item }) => (
  <div className='available-list-item'>
    <div style={{ display: 'flex' }} onClick={() => selectHandler(item)}>
      <input type='checkbox' name='AvailableItem' value={item.value} checked={item.isSelected} disabled={item.isSelected ? 'disabled' : ''} onChange={(event) => {item.isSelected = event.target.checked}} />
    </div>
    <div style={{ paddingLeft: '10px' }}>{label}</div>
  </div>
);
  
const getAvailableItems = (allItems, selectedItems, valueKey) => {
  const selectedItemsObj = selectedItems.reduce((acc, item) => {
    acc[item[valueKey]] = true;
    return acc;
  }, {});

  return allItems.map(item => ({
    ...item,
    isSelected: !!selectedItemsObj[item[valueKey]]
  }));
};

const SelectOrderedSubList = (props) => {
  const [availableItems, setAvailableItems] = useState(getAvailableItems(props.allItems, props.selectedItems, props.valueKey));
  const [selectedItems, setSelectedItems] = useState(props.selectedItems);

  const dragItem = useRef();
  const dragOverItem = useRef();

  const handleDragStart = (e, index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (e, index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    const listCopy = [...selectedItems];
    const dragItemContent = listCopy[dragItem.current];
    listCopy.splice(dragItem.current, 1);
    listCopy.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setSelectedItems(listCopy);
  };

  useEffect(() => {
    setAvailableItems(getAvailableItems(props.allItems, props.selectedItems, props.valueKey));
  }, [props.allItems, props.selectedItems, props.valueKey]);


  const onItemSelected = useCallback((selectedItem) => {
    selectedItem.isSelected = !selectedItem.isSelected
    setAvailableItems(prevAvailableItems => prevAvailableItems.map(item =>
      item[props.valueKey] === selectedItem[props.valueKey] ? { ...item, isSelected: true } : item
    ));
    setSelectedItems(prevSelectedItems => [...prevSelectedItems, selectedItem]);
  }, [props.valueKey]);

  const onItemUnselected = useCallback((unselectedItem) => {
    setAvailableItems(prevAvailableItems => prevAvailableItems.map(item =>
      item[props.valueKey] === unselectedItem[props.valueKey] ? { ...item, isSelected: false } : item
    ));
    setSelectedItems(prevSelectedItems => prevSelectedItems.filter(item => item[props.valueKey] !== unselectedItem[props.valueKey]));
  }, [props.valueKey]);

  const onCancel = useCallback(() => {
    props.onCancel();
  }, [props]);

  const onReset = useCallback(() => {
    props.onReset();
  }, [props]);


  const onSave = useCallback(() => {
    props.onSave(selectedItems);
  }, [selectedItems, props]);

  const renderAvailableListItem = (item, i) => {
    const { displayKey, valueKey } = props;
    if (!displayKey || !valueKey) {
      console.error("ERROR in OrderedSubList! Must supply 'valueKey' and 'displayKey' props when 'allItems' is an array of objects.");
      return null;
    }
    return (
      <AvailableListItem
        selectHandler={onItemSelected}
        label={item[displayKey]}
        item={item}
        key={item[valueKey] + '_' + i}
      />
    );
  };

  const renderSelectedListItem = (item, i) => {
    const { displayKey, valueKey } = props;
    if (!displayKey || !valueKey) {
      console.error("ERROR in OrderedSubList! Must supply 'valueKey' and 'displayKey' props when 'allItems' is an array of objects.");
      return null;
    }
    return (
      <div
        key={item[valueKey] + '_' + i}
        draggable
        onDragStart={(e) => handleDragStart(e, i)}
        onDragEnter={(e) => handleDragEnter(e, i)}
        onDragEnd={handleDragEnd}
        style={{ cursor: 'grab' }}
      >
        <SelectedListItem
          unselectHandler={onItemUnselected}
          label={item[displayKey]}
          item={item}
          canBeUnselected={!item.isLinkedToEditScreen}
        />
      </div>
    );
  };

  return (
    <Modal
      isOpen={props.isOpen}
      title={props.title}
      showTitleBar={true}
      onClose={onCancel}
    >
      <TransitionGroup>
        {props.isOpen && (
          <CSSTransition classNames="dropdown-anim" timeout={250} unmountOnExit>
            <div className='column-selector'>
              <div className='sections'>
                <div className={props.availableListItem}>
                  <div className='section-heading'>{props.availableListLabel}</div>
                  {availableItems.map(renderAvailableListItem)}
                </div>
                <div>
                  <div className='section-heading'>{props.selectedListLabel}</div>
                  {selectedItems.map(renderSelectedListItem)}
                </div>
              </div>
              <div className='buttons'>
                <button className='button primary' onClick={onSave} disabled={!selectedItems || selectedItems.length < 1}>
                  Save
                </button>
                <button className='button plain' onClick={onCancel} disabled={!selectedItems || selectedItems.length < 1}>
                  Cancel
                </button>
                <button className='button plain' onClick={onReset} disabled={!selectedItems || selectedItems.length < 1}>
                  Default
                </button>
              </div>
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </Modal>
  );
};

const SelectedListItem = ({ unselectHandler, label, item, canBeUnselected }) => (
  <div className='selected-list-item'>
    <div style={{ width: '100%' }}>{label}</div>
    {canBeUnselected && (
    <div style={{ marginLeft: '10px', cursor: 'pointer'}} className="bx bx-x" onClick={() => unselectHandler(item)}/>
    )}
  </div>
);

export default SelectOrderedSubList;